/**
 * Governance utility functions for ARN parsing, time formatting,
 * and detail text cleaning.
 *
 * Reusable across any plugin that renders governance data or identity ARNs.
 */

export interface ParsedArn {
  type: string;
  name: string;
  full: string;
}

/**
 * Format an ISO timestamp as a user-friendly locale string.
 * Returns an em-dash for missing or epoch-zero timestamps.
 */
export function formatFriendlyDateTime(iso: string): string {
  if (!iso || iso === '1970-01-01T00:00:00+00:00') {
    return '\u2014';
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '\u2014';

    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return '\u2014';
  }
}

/**
 * Format an ISO timestamp as a relative time string (e.g. "3h ago").
 */
export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();

  if (isNaN(then) || then <= 0) {
    return '\u2014';
  }

  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Format full timestamp with seconds — used in tooltips and audit views.
 */
export function formatFullTimestamp(iso: string): string {
  if (!iso || iso === '1970-01-01T00:00:00+00:00') {
    return 'Timestamp not recorded';
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'Invalid timestamp';
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

/**
 * Parse an EmpowerID Identity Fabric ARN into its constituent parts.
 *
 * ARN format: auth:{type}:{tenant}:{name}
 * Returns null for missing/empty input.
 */
export function parseArn(arn?: string): ParsedArn | null {
  if (!arn || typeof arn !== 'string') return null;
  const parts = arn.split(':').filter((p) => p.length > 0);
  if (parts.length < 2) return { type: 'unknown', name: arn, full: arn };

  const type = parts[1] || 'unknown';
  const name = parts[parts.length - 1] || arn;
  return { type, name, full: arn };
}

/**
 * Get an emoji icon for a given actor type.
 */
export function getActorTypeIcon(type: string): string {
  switch (type) {
    case 'service': return '\uD83E\uDD16';
    case 'account': return '\uD83D\uDC64';
    case 'identity': return '\uD83C\uDD94';
    default: return '\uD83D\uDC64';
  }
}

// Canonical actor type labels per AUTHZ_SCHEMA_CANON_AND_DEPRECATIONS.md
export function getActorTypeLabel(type: string): string {
  switch (type) {
    case 'service': return 'Service';
    case 'account': return 'Account';
    case 'identity': return 'Identity';
    case 'person': return 'Person';
    case 'agent': return 'Agent';
    default: return 'Actor';
  }
}

/**
 * Extract the trailing display name from an ARN.
 *
 * "auth:identity:empowernow:patrick" -> "patrick"
 * "auth:service:empowernow:bff-server" -> "bff-server"
 */
export function extractNameFromArn(arn?: string): string | null {
  if (!arn || typeof arn !== 'string') return null;
  const trimmed = arn.trim();
  if (trimmed.length === 0) return null;

  const parts = trimmed.split(':');
  const validParts = parts.filter((p) => p.length > 0);
  if (validParts.length === 0) return null;

  return validParts[validParts.length - 1];
}

/**
 * Remove ARN references from governance event detail text so the UI
 * can render identity badges separately.
 *
 * "Removed member auth:identity:empowernow:bob" -> "Removed member"
 */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function cleanDetailText(detail: string | undefined, subjectArn?: string): string {
  if (!detail) return '';

  let cleaned = detail;

  if (subjectArn) {
    cleaned = cleaned.replace(new RegExp(`\\s*${escapeRegExp(subjectArn)}\\s*`, 'g'), '');
    const shortArn = subjectArn.replace(/^auth:/, '');
    if (shortArn !== subjectArn) {
      cleaned = cleaned.replace(new RegExp(`\\s*${escapeRegExp(shortArn)}\\s*`, 'g'), '');
    }
  }

  cleaned = cleaned.replace(/\s*auth:[a-z]+:[a-z0-9-]+:[a-z0-9-]+\s*/gi, '');
  cleaned = cleaned.replace(/::+/g, ':').replace(/:\s*$/, '').trim();

  return cleaned || 'Action performed';
}
